import { useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Info,
  Ruler,
  XCircle,
} from "lucide-react";

import type {
  FeasibilityCitedText,
  FeasibilityLimitRow,
  FeasibilityParamCheck,
  FeasibilityResponse,
  FeasibilitySummary,
  ProvisionalReviewRule,
} from "@/api/schemas";
import { Button } from "@/components/ui/button";
import { BadgeStatus, type StatusKind } from "@/components/ui/badge-status";
import { CollapsibleCard } from "@/components/ui/collapsible-card";
import { cn } from "@/lib/utils";
import { countHr } from "@/lib/pluralHr";

/**
 * Step-1 result panel — the verdict the architect came for. Renders the
 * persisted ``FeasibilitySummary`` (not the raw API payload) so the same
 * component shows both a fresh analysis and the saved one when the user
 * revisits the step.
 *
 * Layout (2026-07 redesign — "nalaz, ne dump sekcija"): ONE tinted
 * verdict card at top (label + akcijska rečenica + ključni brojevi + akcija),
 * then subordinate caveat strips (thin left border, not full amber blocks),
 * then a single limits table, findings, and a visible investor memo. All
 * de-duplication is render-side; the persisted summary shape is unchanged.
 */

const OUTCOME_META: Record<string, { kind: StatusKind; label: string; blurb: string }> = {
  ALLOW: {
    kind: "ok",
    label: "Gradnja dopuštena",
    blurb: "Prema dostupnim podacima zahvat je u skladu s planom i propisima.",
  },
  ALLOW_WITH_CONDITIONS: {
    kind: "warn",
    label: "Dopušteno uz uvjete",
    blurb: "Zahvat je moguć, ali uz uvjete navedene u nastavku.",
  },
  MANUAL_REVIEW_REQUIRED: {
    kind: "warn",
    label: "Potrebna ručna provjera",
    blurb: "Automatska analiza nije dovoljna — pregledaj nalaze u nastavku.",
  },
  BLOCKER: {
    kind: "err",
    label: "Postoje prepreke",
    blurb: "Pronađene su prepreke koje sprječavaju zahvat u ovom obliku.",
  },
  INSUFFICIENT_DATA: {
    kind: "warn",
    label: "Nedovoljno podataka",
    blurb: "Za pouzdan zaključak nedostaju podaci — dopuni opis projekta.",
  },
};

const FALLBACK_META = {
  kind: "warn" as StatusKind,
  label: "Rezultat nejasan",
  blurb: "Analiza nije dala jednoznačan zaključak — pregledaj nalaze u nastavku.",
};

export function outcomeMeta(outcome: string) {
  return OUTCOME_META[outcome.toUpperCase()] ?? FALLBACK_META;
}

const BANNER_CLASS: Record<StatusKind, string> = {
  ok: "border-green-200 bg-green-50 text-green-900",
  warn: "border-amber-200 bg-amber-50 text-amber-900",
  err: "border-red-200 bg-red-50 text-red-900",
};

const STATUS_ICON: Record<StatusKind, ReactNode> = {
  ok: <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" />,
  warn: <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />,
  err: <XCircle className="h-5 w-5 flex-shrink-0 text-red-600" />,
};

function fmtNum(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return String(v).replace(".", ",");
}

// ---------------------------------------------------------------------------
// Render-only helpers (NOT pinned by tests — free to change)
// ---------------------------------------------------------------------------

/** Jedinstveni format citata: "Članak 24, stavak 1" → "čl. 24/1"; "Članak 25"
 *  → "čl. 25"; "čl. 24" → "čl. 24". Render-only; summary ostaje netaknut. */
function normalizeCitation(source: string | null | undefined): string | null {
  if (!source) return null;
  const s = source.trim();
  const m = s.match(/(?:članak|čl\.?)\s*(\d+[a-z]?)\s*(?:,?\s*(?:stavak|st\.?)\s*(\d+))?/i);
  if (m) return `čl. ${m[1]}${m[2] ? `/${m[2]}` : ""}`;
  return s;
}

/** Imena parametara koji NISU prošli (status ≠ OK), lowercased. */
function failedParamNames(pv: FeasibilityParamCheck[]): Set<string> {
  return new Set(
    pv.filter((c) => c.status.toUpperCase() !== "OK").map((c) => c.parameter.toLowerCase().trim()),
  );
}

/** Blocker koji samo DUBLA failed parametar (numerički oblik "KIG · projekt …").
 *  Dedupe je na renderu — takav blocker ne prikazujemo zasebno; njegova poruka
 *  živi u akcijskoj rečenici verdikta + ključnim brojevima. */
function isParamBlocker(text: string, names: Set<string>): boolean {
  const head = text.split("·")[0].trim().toLowerCase();
  return names.has(head);
}

/** Redak limita čiji naziv odgovara failed parametru → inline ⚠ marker. */
function matchesFailedParam(rowName: string, names: Set<string>): boolean {
  const n = rowName.toLowerCase();
  for (const p of names) {
    if (p && n.startsWith(p)) return true;
  }
  return false;
}

/** Akcijska rečenica verdikta izvedena iz PRVOG prekoračenog parametra
 *  (rješava "nema što sad"); fallback na generički blurb outcome-a. */
function verdictAction(summary: FeasibilitySummary): string {
  const failed = summary.parameter_validation.find((c) => c.status.toUpperCase() !== "OK");
  if (failed && failed.project_value != null && failed.limit_value != null) {
    const isMin = failed.limit_type === "min";
    const rel = isMin ? "je ispod dopuštenog minimuma" : "prekoračuje dopušteni maksimum";
    const cite = normalizeCitation(
      failed.source_article != null ? `čl. ${failed.source_article}` : null,
    );
    const citeSuffix = cite ? ` (${cite})` : "";
    return (
      `${failed.parameter} ${fmtNum(failed.project_value)} ${rel} ` +
      `${fmtNum(failed.limit_value)}${citeSuffix} — uskladi projekt ili zatraži izmjenu plana.`
    );
  }
  return outcomeMeta(summary.outcome_class).blurb;
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

/** Legacy drafts persisted findings as plain strings — normalise on read. */
export function asCitedText(item: string | FeasibilityCitedText): FeasibilityCitedText {
  if (typeof item === "string") return { text: item, source: null };
  return { text: item.text ?? "", source: item.source ?? null };
}

/** Muted plan-citation suffix — the "svaka tvrdnja nosi citat" promise. */
function SourceRef({ source }: { source: string | null }) {
  if (!source) return null;
  return (
    <span className="ml-1 whitespace-nowrap text-[0.72rem] text-stone-400">— {source}</span>
  );
}

const COUNT_BADGE = "rounded bg-stone-100 px-1.5 py-0.5 text-[0.72rem] text-stone-500";

/** PV — kratki opis zašto staged pravilo NIJE ušlo u presudu. */
function reviewReason(r: ProvisionalReviewRule): string {
  if (r.grounding_status && r.grounding_status !== "grounded") return "citat nepotpun";
  if (r.derived) return "izvedeno, ne doslovno";
  const kig = r.kig_max;
  if (typeof kig === "number" && (kig <= 0 || kig > 1.2)) return "KIG izvan raspona";
  const kisn = r.kisn_max;
  if (typeof kisn === "number" && (kisn <= 0 || kisn > 20)) return "KISN izvan raspona";
  return "za provjeru";
}

// ---------------------------------------------------------------------------
// Verdict card + key numbers + caveats
// ---------------------------------------------------------------------------

/** Ključni brojevi — projekt vs limit po parametru, izravno iz
 *  ``parameter_validation`` (numerička istina). Boja PO parametru. */
function KeyNumbers({ params }: { params: FeasibilityParamCheck[] }) {
  if (params.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {params.map((c, i) => {
        const ok = c.status.toUpperCase() === "OK";
        return (
          <div
            key={`${c.parameter}-${i}`}
            className={cn(
              "rounded-md border bg-white/70 px-2.5 py-1 text-[0.8rem]",
              ok ? "border-stone-200 text-stone-700" : "border-red-300 text-red-800",
            )}
          >
            <span className="font-semibold">{c.parameter}</span>{" "}
            <span className="tabular-nums">
              {fmtNum(c.project_value)}
              <span className="text-stone-400">
                {" "}
                / {c.limit_type === "min" ? "min" : "max"} {fmtNum(c.limit_value)}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function VerdictCard({
  summary,
  onContinue,
}: {
  summary: FeasibilitySummary;
  onContinue?: () => void;
}) {
  const meta = outcomeMeta(summary.outcome_class);
  return (
    <div className={cn("rounded-lg border px-5 py-4", BANNER_CLASS[meta.kind])}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {STATUS_ICON[meta.kind]}
          <span className="text-[1.05rem] font-bold leading-tight">{meta.label}</span>
        </div>
        {onContinue && (
          <Button type="button" onClick={onContinue}>
            Nastavi na podatke o projektu
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
      <p className="mt-2 text-[0.9rem] font-medium leading-snug">{verdictAction(summary)}</p>
      <KeyNumbers params={summary.parameter_validation} />
      <div className="mt-3 text-[0.72rem] opacity-60">
        {summary.municipality_name}
        {summary.plan_version && <> · plan {summary.plan_version}</>}
        {" · provjereno "}
        {new Date(summary.checkedAt).toLocaleString("hr-HR")}
      </div>
    </div>
  );
}

/** Caveat trake (freshness / provisional / model) — vizualno PODREĐENE
 *  verdiktu: tanka lijeva bordura, bez punog tinta. Rješava amber-overload. */
function CaveatStrip({ summary }: { summary: FeasibilitySummary }) {
  const items: Array<{ border: string; icon: ReactNode; text: ReactNode }> = [];
  if (summary.provisional) {
    items.push({
      border: "border-ind-400",
      icon: <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-ind-500" />,
      text: (
        <>
          <strong className="font-semibold">Provizoran verdikt</strong> — AI je pročitao
          učitani plan, još nije potvrđen. Provjeri limite i klikni „Potvrdi i spremi" da
          postanu mjerodavni.
        </>
      ),
    });
  }
  if (summary.plan_freshness?.stale && summary.plan_freshness.warning) {
    items.push({
      border: "border-amber-400",
      icon: <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-500" />,
      text: (
        <>
          <strong className="font-semibold">Tekst plana možda nije najnoviji.</strong>{" "}
          {summary.plan_freshness.warning}
        </>
      ),
    });
  }
  if (summary.upu_gap?.l1?.warning) {
    // M4a — L1: plan mjerodavne zone delegira detaljnu razradu na UPU/DPU
    // (per-čestica, citirano). Ne mijenja presudu.
    items.push({
      border: "border-amber-400",
      icon: <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-500" />,
      text: (
        <>
          <strong className="font-semibold">Zona se razrađuje kroz UPU/DPU.</strong>{" "}
          {summary.upu_gap.l1.warning}
        </>
      ),
    });
  }
  if (summary.upu_gap?.l2?.warning) {
    // M4a — L2: općina ima registrirane UPU/DPU koje ne držimo. Tiha napomena
    // o pokrivenosti — ne nadglasava presudu.
    items.push({
      border: "border-stone-300",
      icon: <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-stone-400" />,
      text: summary.upu_gap.l2.warning,
    });
  }
  if (summary.model_warning) {
    items.push({
      border: "border-stone-300",
      icon: <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-stone-400" />,
      text: summary.model_warning,
    });
  }
  if (items.length === 0) return null;
  return (
    <div className="space-y-1">
      {items.map((it, i) => (
        <div
          key={i}
          className={cn("flex gap-2 border-l-2 py-1 pl-3 text-[0.76rem] text-stone-600", it.border)}
        >
          {it.icon}
          <span>{it.text}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Findings / limits / provisional review
// ---------------------------------------------------------------------------

/** Prepreke (residual — samo one koje nisu numerički param-dupli). */
function BlockerList({ items }: { items: Array<string | FeasibilityCitedText> }) {
  const rows = items.map(asCitedText).filter((r) => r.text);
  if (rows.length === 0) return null;
  return (
    <div className="rounded-md border border-red-200 bg-red-50/60 px-3 py-1">
      <div className="py-1 text-[0.72rem] font-bold uppercase tracking-[0.04em] text-red-700">
        Prepreke <span className="font-normal text-red-400">({rows.length})</span>
      </div>
      <ul>
        {rows.map((item, i) => (
          <li
            key={i}
            className="border-b border-red-100 py-1.5 text-[0.85rem] text-stone-700 last:border-0"
          >
            {item.text}
            <SourceRef source={normalizeCitation(item.source)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Group limit rows by their plan context (neposredna/posredna primjena…),
 * preserving first-seen order so the engine's ordering survives. */
export function groupLimitsByContext(
  items: Array<string | FeasibilityLimitRow>,
): Array<{ context: string; rows: FeasibilityLimitRow[] }> {
  const groups: Array<{ context: string; rows: FeasibilityLimitRow[] }> = [];
  const index = new Map<string, number>();
  for (const raw of items) {
    const row = asLimitRow(raw);
    if (!row.value && !row.name) continue;
    const key = row.context ?? "";
    let at = index.get(key);
    if (at === undefined) {
      at = groups.length;
      index.set(key, at);
      groups.push({ context: key, rows: [] });
    }
    groups[at].rows.push(row);
  }
  return groups;
}

/** Legacy drafts persisted conditions as plain strings — normalise on read. */
export function asLimitRow(item: string | FeasibilityLimitRow): FeasibilityLimitRow {
  if (typeof item === "string") return { name: "", value: item, context: null, source: null };
  return {
    name: item.name ?? "",
    value: item.value ?? "",
    context: item.context ?? null,
    source: item.source ?? null,
  };
}

/** Jedinstvena "Limiti i uvjeti plana" tablica — spaja tekstualne uvjete +
 *  kontekst (neposredna/posredna) bez ponavljanja; inline ⚠ uz prekoračeni
 *  parametar povezuje tablicu s verdiktom (nema zasebne "Prepreke" duplikacije). */
function LimitList({
  items,
  failedNames,
}: {
  items: Array<string | FeasibilityLimitRow>;
  failedNames: Set<string>;
}) {
  const groups = groupLimitsByContext(items);
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  if (total === 0) return null;
  return (
    <CollapsibleCard
      icon={<Ruler className="h-4 w-4 text-stone-400" />}
      title="Limiti i uvjeti plana"
      summary={<span className={COUNT_BADGE}>{total}</span>}
    >
      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g.context || "(bez konteksta)"}>
            {g.context && (
              <div className="pb-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.04em] text-stone-400">
                {g.context}
              </div>
            )}
            <ul>
              {g.rows.map((row, i) => {
                const flagged = !!row.name && matchesFailedParam(row.name, failedNames);
                return (
                  <li
                    key={`${row.name}-${i}`}
                    className="flex flex-wrap items-baseline gap-x-2 border-b border-stone-100 py-1.5 text-[0.85rem] text-stone-700 last:border-0"
                  >
                    {row.name && <span className="font-semibold">{row.name}</span>}
                    <span>{row.value}</span>
                    {flagged && <BadgeStatus kind="err">prekoračeno</BadgeStatus>}
                    <span className="ml-auto">
                      <SourceRef source={normalizeCitation(row.source)} />
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </CollapsibleCard>
  );
}

/** PV — staged pravila koja AI NIJE uzeo u presudu (ne-grounded / derived /
 *  izvan raspona). Prikazana „za provjeru" s doslovnim citatom (value_spans). */
function ProvisionalReviewList({ rules }: { rules: ProvisionalReviewRule[] }) {
  const [open, setOpen] = useState(false);
  if (rules.length === 0) return null;
  return (
    <section className="rounded-md border border-stone-200 bg-white">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-[0.85rem] font-semibold text-stone-700">
          Pročitano, ali nije uzeto u verdikt
          <span className="ml-2 font-normal text-[0.75rem] text-stone-400">
            {countHr(rules.length, "pravilo", "pravila", "pravila")} za provjeru — citat
            nepotpun ili vrijednost izvan raspona
          </span>
        </span>
        <ChevronDown
          className={cn("h-4 w-4 text-stone-400 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <ul className="border-t border-stone-100 px-4 py-2">
          {rules.map((r, i) => {
            const spans = Object.values(r.value_spans ?? {}).filter(Boolean);
            return (
              <li key={i} className="border-b border-stone-50 py-2 last:border-0">
                <div className="flex flex-wrap items-center gap-2">
                  <BadgeStatus kind="warn">{reviewReason(r)}</BadgeStatus>
                  <span className="text-[0.82rem] text-stone-700">
                    {r.rule_subject_code ?? "—"}
                  </span>
                  {r.source_article != null && (
                    <span className="text-[0.72rem] text-stone-400">
                      {normalizeCitation(`čl. ${r.source_article}`)}
                    </span>
                  )}
                </div>
                {spans.length > 0 && (
                  <p className="mt-0.5 text-[0.75rem] italic text-stone-500">
                    {spans.map((s) => `„${s}"`).join(" · ")}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

interface FeasibilityResultProps {
  summary: FeasibilitySummary;
  /** Omitted when the result renders inline on the data page — there is
   * no separate step to continue to anymore. */
  onContinue?: () => void;
}

export function FeasibilityResult({ summary, onContinue }: FeasibilityResultProps) {
  const failedNames = failedParamNames(summary.parameter_validation);
  // Dedupe: blockeri koji samo dublaju prekoračeni parametar žive u verdiktu,
  // ne kao zasebna sekcija. Ostaju samo pravi (ne-param) blockeri.
  const residualBlockers = summary.blockers.filter(
    (b) => !isParamBlocker(asCitedText(b).text, failedNames),
  );
  const findingRows = summary.findings.map(asCitedText).filter((r) => r.text);

  return (
    <div className="mt-6 space-y-3" data-testid="feasibility-result">
      <VerdictCard summary={summary} onContinue={onContinue} />

      <CaveatStrip summary={summary} />

      {residualBlockers.length > 0 && <BlockerList items={residualBlockers} />}

      <LimitList items={summary.conditions} failedNames={failedNames} />

      {findingRows.length > 0 && (
        <CollapsibleCard
          icon={<Info className="h-4 w-4 text-stone-400" />}
          title="Ključni nalazi"
          summary={<span className={COUNT_BADGE}>{findingRows.length}</span>}
        >
          <ul>
            {findingRows.map((item, i) => (
              <li
                key={i}
                className="border-b border-stone-100 py-1.5 text-[0.85rem] text-stone-700 last:border-0"
              >
                {item.text}
                <SourceRef source={normalizeCitation(item.source)} />
              </li>
            ))}
          </ul>
        </CollapsibleCard>
      )}

      {summary.provisional && (summary.provisional_review_rules?.length ?? 0) > 0 && (
        <ProvisionalReviewList rules={summary.provisional_review_rules!} />
      )}

      {summary.memo_text && (
        <CollapsibleCard
          defaultOpen
          title="Sažetak za investitora"
          summary="automatski — mjerodavni su citirani limiti iznad"
        >
          <p className="whitespace-pre-wrap font-sans text-[0.85rem] leading-relaxed text-stone-700">
            {summary.memo_text}
          </p>
        </CollapsibleCard>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Response → persisted summary  (data-mapiranje — PINANO testovima, ne dirati)
// ---------------------------------------------------------------------------

/** The engine cites via ``source_reference`` (LLM shapes) or
 * ``source_article`` (pre-LLM numeric blockers). */
function extractSource(obj: Record<string, unknown>): string | null {
  const ref = obj.source_reference ?? obj.source ?? null;
  if (typeof ref === "string" && ref.trim()) {
    // Engine sometimes emits "Članak 60, stavak unknown" — keep the article,
    // drop the broken-looking tail.
    return ref.trim().replace(/,?\s*stavak unknown$/i, "");
  }
  const art = obj.source_article;
  if (typeof art === "string" && art.trim()) return `čl. ${art.trim()}`;
  if (typeof art === "number") return `čl. ${art}`;
  return null;
}

/** Best-effort cited text for an LLM-shaped finding/blocker entry. */
function citedFinding(item: unknown): FeasibilityCitedText | null {
  if (item == null) return null;
  if (typeof item === "string") {
    const t = item.trim();
    return t ? { text: t, source: null } : null;
  }
  if (typeof item !== "object") return { text: String(item), source: null };
  const obj = item as Record<string, unknown>;
  const source = extractSource(obj);
  for (const key of ["statement", "description", "reason", "text", "message", "condition"]) {
    const v = obj[key];
    if (typeof v === "string" && v.trim()) return { text: v.trim(), source };
  }
  // Numeric-blocker shape from the pre-LLM checker.
  if (typeof obj.parameter === "string") {
    const parts = [obj.parameter];
    if (obj.project_value != null) parts.push(`projekt ${fmtNum(obj.project_value as number)}`);
    if (obj.limit_value != null) {
      parts.push(
        `limit ${obj.limit_type === "min" ? "min" : "max"} ${fmtNum(obj.limit_value as number)}`,
      );
    }
    // LLM blocker shape uses allowed_value instead of limit_value.
    if (obj.limit_value == null && obj.allowed_value != null) {
      parts.push(`dopušteno ${String(obj.allowed_value)}`);
    }
    return { text: parts.join(" · "), source };
  }
  return null;
}

function citedAll(items: unknown[]): FeasibilityCitedText[] {
  const out: FeasibilityCitedText[] = [];
  for (const item of items) {
    const c = citedFinding(item);
    if (c && !out.some((x) => x.text === c.text && x.source === c.source)) out.push(c);
  }
  return out;
}

/** One limit row from an ``conditions_and_limits`` entry
 * (``{name, value, context, source_reference}`` per the LLM schema);
 * plain strings and odd shapes degrade to a value-only row. */
function limitRow(item: unknown): FeasibilityLimitRow | null {
  if (item == null) return null;
  if (typeof item === "string") {
    const t = item.trim();
    return t ? { name: "", value: t, context: null, source: null } : null;
  }
  if (typeof item !== "object") {
    return { name: "", value: String(item), context: null, source: null };
  }
  const obj = item as Record<string, unknown>;
  const name = typeof obj.name === "string" ? obj.name.trim() : "";
  const value =
    typeof obj.value === "string"
      ? obj.value.trim()
      : obj.value != null
        ? String(obj.value)
        : "";
  if (!name && !value) {
    // Unknown shape — fall back to the finding extractor so nothing is lost.
    const c = citedFinding(item);
    return c ? { name: "", value: c.text, context: null, source: c.source } : null;
  }
  const context = typeof obj.context === "string" && obj.context.trim() ? obj.context.trim() : null;
  return { name, value, context, source: extractSource(obj) };
}

function limitRows(items: unknown[]): FeasibilityLimitRow[] {
  const out: FeasibilityLimitRow[] = [];
  for (const item of items) {
    const r = limitRow(item);
    if (r) out.push(r);
  }
  return out;
}

export function toFeasibilitySummary(res: FeasibilityResponse): FeasibilitySummary {
  return {
    outcome_class: res.outcome_class,
    municipality_name: res.municipality_name || res.municipality_code || "",
    plan_version: res.plan_version ?? "",
    plan_freshness: res.plan_freshness ?? null,
    upu_gap: res.upu_gap ?? null,
    // PV — verdikt nad učitanim (staged) planom; UI diže amber „provizorno" traku.
    provisional: res.provisional ?? false,
    provisional_review_rules: res.provisional_review_rules ?? [],
    memo_text: res.memo_text,
    parameter_validation: res.parameter_validation,
    blockers: citedAll([...res.pre_llm_blockers, ...res.llm_output.blockers]),
    conditions: limitRows(res.llm_output.conditions_and_limits),
    findings: citedAll(res.llm_output.key_findings),
    model_warning: res.model_warning ?? null,
    checkedAt: new Date().toISOString(),
  };
}
